import {
  Action,
  PlaybackOptions,
  PlaybackStatistics,
  Player,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  TrackInfo,
  TrackVariantInfo,
  PLAYBACK_STATES,
  TrackType,
} from '@quickplay/player-interface';
import { AnyFunc, eventObject, Error, EventEmitter, createEventEmitter } from '@quickplay/foundation';
import { TritonPlayerBuilder } from '../TiritonPlayerBuilder';
import { PlayerFactory } from './PlayerFactory';
import { SideBandMetadataAdapter } from '../sidebandMetadata/internal/SideBandMetadataAdapter';
import { SideBandMetadataFactory } from '../sidebandMetadata/internal/SbmFactory';
import { TritonAudioStreamConnectionConfig } from '../TritonAudioStreamConnection';

export class DefaultTritonPlayer implements Player {
  private audioStreamConnectionConfig: TritonAudioStreamConnectionConfig;
  private player_: Player;
  private eventEmitter_: EventEmitter = createEventEmitter();
  private sbmAdapter_: SideBandMetadataAdapter | undefined;
  private isPlaybackStarted_ = false;

  constructor(builder: TritonPlayerBuilder) {
    this.audioStreamConnectionConfig = builder.audioStreamConnectionConfig;
    this.sbmAdapter_ = SideBandMetadataFactory.createSideMetadataAdapter(
      builder.contentUrl,
      this.audioStreamConnectionConfig,
      builder.configuredLogger,
    );
    this.player_ = PlayerFactory.createPlayer(builder, this.sbmAdapter_?.getContentUrl());
    this.subscribeToInternalPlayerEvents();
  }
  get availableBitrates(): number[] {
    return this.player_.availableBitrates;
  }

  get currentEpochTime(): number {
    return this.player_.currentEpochTime;
  }

  get currentTime(): number {
    return this.player_.currentTime;
  }

  get duration(): number {
    return this.player_.duration;
  }

  get internalPlayerName(): string {
    return this.player_.internalPlayerName;
  }

  get internalPlayerVersion(): string {
    return this.player_.internalPlayerVersion;
  }

  get isBuffering(): boolean {
    return this.player_.isBuffering;
  }

  get isLive(): boolean {
    return this.player_.isLive;
  }

  get isMute(): boolean | undefined {
    return this.player_.isMute;
  }

  get playbackOptions(): PlaybackOptions | undefined {
    return this.player_.playbackOptions;
  }

  get isSeeking(): boolean {
    return this.player_.isSeeking;
  }

  get maxBitrate(): number | undefined {
    return this.player_.maxBitrate;
  }

  set maxBitrate(maxBitrate) {
    this.player_.maxBitrate = maxBitrate;
  }

  get minBitrate(): number | undefined {
    return this.player_.minBitrate;
  }

  set minBitrate(minBitrate) {
    this.player_.minBitrate = minBitrate;
  }

  get playbackRate(): number {
    return this.player_.playbackRate;
  }
  set playbackRate(playbackRate) {
    this.player_.playbackRate = playbackRate;
  }

  get playbackStatistics(): PlaybackStatistics | undefined {
    return this.player_.playbackStatistics;
  }

  get playerState(): string {
    return this.player_.playerState;
  }

  get rawLibrary(): any {
    return this.player_.rawLibrary;
  }

  get rawPlayer(): any {
    return this.player_.rawPlayer;
  }

  get remainingTime(): number {
    return 0;
  }

  get volume(): number {
    return this.player_.volume;
  }

  set volume(volumeLevel: number) {
    this.player_.volume = volumeLevel;
  }

  abort(error: Error): void {
    this.player_.abort(error);
  }

  send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
    this.player_.send(eventName, action, metadata);
  }

  currentOffsetFromLiveEdge(): number {
    return this.player_.currentOffsetFromLiveEdge();
  }

  discardAdBreak(): void {
    //TODO: implement
  }

  resize(_width: number, _height: number, _isFullscreen: boolean): void {
    // no-op
  }
  getActiveTracks(): TrackInfo[] {
    return this.player_.getActiveTracks();
  }

  getVariantTracks(): TrackVariantInfo[] {
    return this.player_.getVariantTracks();
  }

  getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
    return this.player_.getAvailableTrackVariants(trackType);
  }

  getAllTracks(): TrackInfo[] {
    return this.player_.getAllTracks();
  }

  load(): void {
    this.player_.load();
  }

  muteOrUnmute(): void {
    this.player_.muteOrUnmute && this.player_.muteOrUnmute();
  }

  pause(): void {
    this.player_.pause();
  }

  play(): void {
    this.player_.play();
  }

  seek(seekPoint: number): void {
    this.player_.seek(seekPoint);
  }

  seekToLiveEdge(): void {
    this.player_.seekToLiveEdge();
  }

  seekableRange(): SeekRangeInfo {
    if (this.player_.isLive) {
      return this.player_.seekableRange();
    }

    return {
      start: 0,
      end: this.duration,
    };
  }

  selectTrack(track?: TrackInfo): void {
    this.player_.selectTrack(track);
  }

  setRequestInterceptor(requestInterceptor: (requestType: RequestType, request: any) => void): void {
    this.player_.setRequestInterceptor(requestInterceptor);
  }

  setResponseInterceptor(responseInterceptor: (requestType: RequestType, response: any) => void): void {
    this.player_.setResponseInterceptor(responseInterceptor);
  }

  getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
    return this.player_.getThumbnail(position);
  }

  stop(): Promise<void> {
    this.sbmAdapter_ && this.sbmAdapter_.release();
    return this.player_.stop();
  }

  accessFunctionHandler(json: string): void {
    this.player_.accessFunctionHandler(json);
  }

  setPlaybackOptions(playbackOptions: PlaybackOptions): void {
    this.player_.setPlaybackOptions(playbackOptions);
  }
  restrictQuality(enableRestriction: boolean): void {
    this.player_.restrictQuality(enableRestriction);
  }

  availableMimeTypes(trackType: TrackType): Array<string> {
    return this.player_.availableMimeTypes(trackType);
  }

  unloadAndSavePreload(): Promise<void> {
    // no-op
    return Promise.resolve();
  }

  restoreFromPreload(): void {
    // no-op
  }

  setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
    return this.player_.setPreferredMimeTypes(trackType, mimeTypes);
  }

  subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.eventEmitter_.subscribe(eventName, callback);
    this.player_.subscribe(eventName, callback);
    this.sbmAdapter_?.subscribe(eventName, callback);
  }

  unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.eventEmitter_.unsubscribe(eventName, callback);
    this.player_.unsubscribe(eventName, callback);
    this.sbmAdapter_?.unsubscribe(eventName, callback);
  }

  private subscribeToInternalPlayerEvents = (): void => {
    this.player_.subscribe('playbackstatechanged', this.playbackStateChanged_);
    this.player_.subscribe('progressupdate', this.onProgressUpdate_);
    this.player_.subscribe('aborted', this.releaseSession);
    this.player_.subscribe('error', this.releaseSession);
  };

  private unsubscribeFromInternalPlayerEvents = (): void => {
    this.player_.unsubscribe('playbackstatechanged', this.playbackStateChanged_);
    this.player_.unsubscribe('progressupdate', this.onProgressUpdate_);
    this.player_.unsubscribe('aborted', this.releaseSession);
    this.player_.unsubscribe('error', this.releaseSession);
  };

  private releaseSession = (): void => {
    if (this.sbmAdapter_) {
      this.sbmAdapter_.release();
      this.sbmAdapter_ = undefined;
    }

    // unsubscribe from all internal player events
    this.unsubscribeFromInternalPlayerEvents();
  };

  private playbackStateChanged_ = (playbackState: PLAYBACK_STATES): void => {
    switch (playbackState) {
      case PLAYBACK_STATES.STARTED: {
        if (!this.isPlaybackStarted_ && this.sbmAdapter_) {
          this.sbmAdapter_.init(this.player_);
          this.isPlaybackStarted_ = true;
        }
        break;
      }
      case PLAYBACK_STATES.STOPPED: {
        this.releaseSession();
        break;
      }
    }
  };

  private onProgressUpdate_ = (): void => {
    if (!this.sbmAdapter_) {
      return;
    }
    this.sbmAdapter_.checkTritonStreamSbmMetadata();
  };
}
